// Pure geometry helpers for laying out the wheel as SVG.
// Ported 1:1 from the original prototype's math (ai-promo/emotion-wheel.html)
// - same radii, same angle scheme (0deg = top, clockwise), same segment/label
// sizing - just restructured to return plain data instead of mutating the DOM.

const toPoint = (cx, cy, r, angleDeg) => {
  const a = (angleDeg * Math.PI) / 180
  return [cx + r * Math.sin(a), cy - r * Math.cos(a)]
}

// Uniform across every ring per the source photo - the real wheel doesn't
// shrink text toward the outside, so core/mid/outer all render at the same
// size (see styles.less for the font-family change too).
const LABEL_SIZE = 11

const sectorPath = (cx, cy, ri, ro, a1, a2) => {
  const p1 = toPoint(cx, cy, ri, a1)
  const p2 = toPoint(cx, cy, ro, a1)
  const p3 = toPoint(cx, cy, ro, a2)
  const p4 = toPoint(cx, cy, ri, a2)
  return (
    `M${p1[0]} ${p1[1]}` +
    `L${p2[0]} ${p2[1]}` +
    `A${ro} ${ro} 0 0 1 ${p3[0]} ${p3[1]}` +
    `L${p4[0]} ${p4[1]}` +
    `A${ri} ${ri} 0 0 0 ${p1[0]} ${p1[1]}Z`
  )
}

// Builds every segment path + label position/rotation + a label -> {x, y}
// centroid lookup (used to place the seeded "room" dots on reveal).
export const buildWheel = ({ cores, geometry }) => {
  const [cx, cy] = geometry.center
  const R_CORE = geometry.radii.core
  const R_MID = geometry.radii.branchOuter
  const R_OUT = geometry.radii.outerOuter

  const segments = []
  const labels = []
  const centroids = {}
  const labelAngles = {}

  const addLabel = (text, r, angleMid, cls) => {
    const [x, y] = toPoint(cx, cy, r, angleMid)
    centroids[text] = { x, y }
    labelAngles[text] = angleMid
    // Best-guess port: I don't have the reference photo in this session, so
    // this rotates core labels the same radial way as mid/outer ones
    // (previously core text stayed flat/horizontal). Flag if it doesn't
    // match the source image and I'll adjust.
    const rotation = angleMid > 180 ? angleMid + 90 : angleMid - 90
    labels.push({ text, x, y, size: LABEL_SIZE, cls, rotation })
  }

  cores.forEach((core, ci) => {
    const c0 = ci * 60
    const c1 = c0 + 60
    const { colors } = core

    segments.push({
      id: `core-${core.name}`,
      d: sectorPath(cx, cy, 0, R_CORE, c0, c1),
      fill: colors.core,
      emotion: core.name,
    })

    core.branches.forEach((branch, bi) => {
      const b0 = c0 + bi * 12
      const b1 = b0 + 12

      segments.push({
        id: `branch-${branch.name}`,
        d: sectorPath(cx, cy, R_CORE, R_MID, b0, b1),
        fill: colors.branch,
        emotion: branch.name,
      })

      branch.outer.forEach((outerLabel, oi) => {
        const o0 = b0 + oi * 6
        const o1 = o0 + 6

        segments.push({
          id: `outer-${outerLabel}`,
          d: sectorPath(cx, cy, R_MID, R_OUT, o0, o1),
          fill: colors.outer,
          emotion: outerLabel,
        })
        addLabel(outerLabel, (R_MID + R_OUT) / 2, (o0 + o1) / 2, 'outer')
      })

      addLabel(branch.name, (R_CORE + R_MID) / 2, (b0 + b1) / 2, 'mid')
    })

    addLabel(core.name, R_CORE * 0.55, c0 + 30, 'core')
  })

  return { segments, labels, centroids, labelAngles }
}

const mod = (n, m) => ((n % m) + m) % m

// Given the raw wedge angles (0deg = top, clockwise - same scheme as the
// rest of this file) for a sequence of labels that should each land at the
// top in order, returns a cumulative rotation (in degrees, to apply as
// `transform: rotate()` on the whole wheel) for each step, starting with 0
// for "no rotation yet". Each step picks the rotation closest to the
// previous one (rather than always the shortest rotation from zero), so
// consecutive locks turn the wheel a little further in one direction
// instead of springing back and forth.
export const computeLockRotations = (targetAngles) => {
  const rotations = [0]
  let cumulative = 0
  targetAngles.forEach((angle) => {
    const desired = mod(-angle, 360) // one solution in [0, 360)
    const rotation = desired + Math.round((cumulative - desired) / 360) * 360
    rotations.push(rotation)
    cumulative = rotation
  })
  return rotations
}

// Deterministic stand-in for Math.random(), seeded from a string. Needed
// because this feeds SVG attributes rendered both on the server (Gatsby SSR)
// and the client - real Math.random() would produce a different jitter each
// time, so the server-rendered markup and the client's first render would
// disagree and React would throw a hydration mismatch. Doesn't need to be
// cryptographically anything, just stable and reasonably spread out.
const seededUnit = (seed) => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0
  }
  // Map to [0, 1) - the >>> 0 forces an unsigned 32-bit value first.
  return ((hash >>> 0) % 10000) / 10000
}

// Flattens the per-person response data into one small dot per response,
// jittered around its emotion label's centroid so dots on the same label
// don't stack exactly on top of each other. Any metadata on the entry
// (person, color, note) is passed through untouched for rendering/tooltips.
export const buildTeamDots = (teamResponses, centroids) => {
  const dots = []
  teamResponses.forEach(({ person, color, responses }) => {
    responses.forEach(({ emotion, note }) => {
      const centroid = centroids[emotion]
      if (!centroid) return
      const seed = `${person}-${emotion}`
      const jx = (seededUnit(`${seed}-x`) - 0.5) * 26
      const jy = (seededUnit(`${seed}-y`) - 0.5) * 26
      dots.push({
        x: centroid.x + jx,
        y: centroid.y + jy,
        person,
        color,
        emotion,
        note,
      })
    })
  })
  return dots
}
